package org.vreo.devicehub.features.device.presentation.addDevice

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.core.domain.device.device.ProductType
import org.vreo.devicehub.features.core.presentation.components.largeDropdownMenu.LargeDropdownMenu

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.add_device_button_title
import devicehub.composeapp.generated.resources.device_details_linking_password
import devicehub.composeapp.generated.resources.device_details_serial_number_label
import devicehub.composeapp.generated.resources.add_device_tag_label
import devicehub.composeapp.generated.resources.device_details_type_label
import devicehub.composeapp.generated.resources.add_device_title
import devicehub.composeapp.generated.resources.error_general_title
import org.vreo.devicehub.features.device.presentation.components.toUiText

/**
 * Add device screen root
 *
 * @param viewModel view model
 * @param onAddUserSuccess callback to be called on successful ad dalvik operation
 * @param onBackNavigation callback to be called on back navigation
 */
@Composable
fun AddDeviceScreenRoot(
    viewModel: AddDeviceViewModel = koinViewModel(),
    onAddUserSuccess: () -> Unit,
    onBackNavigation: () -> Unit
) {
    val state: AddDeviceState by viewModel.state.collectAsStateWithLifecycle()

    AddDeviceScreenView(
        state = state,
        onAction = { action ->
            val actionRequest = when (action) {
                is AddDeviceActions.OnAddDevice -> {
                    action.copy(
                        callback = onAddUserSuccess
                    )
                }

                is AddDeviceActions.OnBackNavigation -> {
                    onBackNavigation()
                    action
                }

                else -> action
            }
            viewModel.onAction(actionRequest)
        }
    )
}

/**
 * Add device screen view
 *
 * @param state state to present
 * @param onAction action handler
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddDeviceScreenView(
    state: AddDeviceState,
    onAction: (AddDeviceActions) -> Unit,
) {
    val scrollState = rememberScrollState()
    val focusManager = LocalFocusManager.current

    Scaffold(
        topBar = {
            TopAppBar(
                title = {},
                navigationIcon = {
                    IconButton(
                        onClick = {
                            onAction(AddDeviceActions.OnBackNavigation())
                        }
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = null,
                        )
                    }
                }
            )
        }
    ) { innerPadding ->
        Box(
            contentAlignment = Alignment.TopCenter,
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .padding(16.dp)
                    .verticalScroll(scrollState)
            ) {
                Text(
                    text = stringResource(Res.string.add_device_title),
                    style = MaterialTheme.typography.displaySmall.copy(
                        fontWeight = FontWeight.Bold
                    )
                )

                HorizontalDivider(
                    modifier = Modifier
                        .padding(bottom = 8.dp)
                )

                Column(
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    modifier = Modifier
                        .widthIn(max = 900.dp)
                ) {

                    LargeDropdownMenu(
                        label = stringResource(Res.string.device_details_type_label),
                        items = ProductType.VALUES.map { item ->
                            item.toUiText().asString()
                        },
                        selectedIndex = state.selectedProductIndex,
                        onItemSelected = {
                            onAction(AddDeviceActions.OnUpdateSelectedProduct(it))
                        },
                    )

                    OutlinedTextField(
                        value = state.serialNumber,
                        onValueChange = {
                            onAction(AddDeviceActions.OnUpdateSerialNumber(it))
                        },
                        label = { Text(stringResource(Res.string.device_details_serial_number_label)) },
                        singleLine = true,
                        isError = state.invalidSerialNumber,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Text,
                            imeAction = ImeAction.Next
                        ),
                        keyboardActions = KeyboardActions(
                            onNext = { focusManager.moveFocus(FocusDirection.Down) }
                        ),
                        modifier = Modifier.fillMaxWidth()
                    )

                    OutlinedTextField(
                        value = state.tag,
                        onValueChange = {
                            onAction(AddDeviceActions.OnUpdateTag(it))
                        },
                        label = { Text(stringResource(Res.string.add_device_tag_label)) },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Text,
                            imeAction = ImeAction.Next
                        ),
                        keyboardActions = KeyboardActions(
                            onNext = { focusManager.moveFocus(FocusDirection.Down) }
                        ),
                        modifier = Modifier.fillMaxWidth()
                    )

                    OutlinedTextField(
                        value = state.linkingPassword,
                        onValueChange = {
                            onAction(AddDeviceActions.OnUpdatePassword(it))
                        },
                        label = { Text(stringResource(Res.string.device_details_linking_password)) },
                        isError = state.invalidPassword,
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Text,
                            imeAction = ImeAction.Next
                        ),
                        keyboardActions = KeyboardActions(
                            onNext = { focusManager.moveFocus(FocusDirection.Down) }
                        ),
                        modifier = Modifier.fillMaxWidth()
                    )

                    Spacer(
                        modifier = Modifier.padding(16.dp)
                    )

                    Button(
                        onClick = {
                            onAction(AddDeviceActions.OnAddDevice())
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(24.dp)
                    ) {
                        Text(stringResource(Res.string.add_device_button_title))
                    }

                    Spacer(
                        modifier = Modifier.padding(32.dp)
                    )
                }
            }

            // Conditional section for displaying loading and error states
            if (state.isLoading) {
                LoadingDialog()
            } else if (state.error != null) {
                ErrorDialog(
                    dialogTitle = stringResource(Res.string.error_general_title),
                    dialogText = state.error.asString(),
                    onDismissRequest = {
                        onAction(AddDeviceActions.OnClearError())
                    },
                    onConfirmation = {
                        onAction(AddDeviceActions.OnClearError())
                    }
                )
            }
        }
    }
}

@Preview
@Composable
fun AddDeviceScreenViewPreview() {
    MaterialTheme {
        AddDeviceScreenView(
            state = AddDeviceState(),
            onAction = {},
        )
    }
}